import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '../store/hooks';
import cometService from '../services/cometService';
import { useToast } from './ToastContext';

export interface OrderStatusUpdateEvent {
  orderId: number;
  orderCode?: string;
  orderStatus?: string;
  status?: string;
  updatedOn?: unknown;
  approxWaitTime?: number;
  userOrder?: string;
  userOrderItems?: string;
  deliveryStatus?: string;
  /** Set when flag is ORDER_ITEM_STATUS_CHANGED */
  orderItemId?: number;
  itemName?: string;
  itemStatus?: string;
  itemQuantity?: number;
  isItemStatusChange?: boolean;
  /** False when this line moved as part of a whole-order action. */
  announce?: boolean;
}

export interface OrderWaitTimeUpdateEvent {
  orderId: number;
  approxWaitTime?: number;
  approxWaitDateTime?: string;
}

type TableRequestStatusListener = (payload: {
  approved?: boolean;
  requestId?: number;
}) => void;

type OrderStatusListener = (payload: OrderStatusUpdateEvent) => void;
type OrderWaitTimeListener = (payload: OrderWaitTimeUpdateEvent) => void;

type QueueTokenUpdateListener = (payload: {
  tokenLabel?: string;
  status?: string;
  position?: number;
  queueType?: string;
  publicRef?: string;
}) => void;

export interface RewardCardIssuedEvent {
  cardId?: number;
  flowType?: string;
  rewardLabel?: string;
  badgeCount?: number;
}

type RewardCardIssuedListener = (payload: RewardCardIssuedEvent) => void;

interface RealtimeContextValue {
  subscribeTableRequestStatus: (listener: TableRequestStatusListener) => () => void;
  subscribeOrderStatus: (listener: OrderStatusListener) => () => void;
  subscribeOrderWaitTime: (listener: OrderWaitTimeListener) => () => void;
  subscribeQueueTokenUpdates: (listener: QueueTokenUpdateListener) => () => void;
  subscribeRewardCardIssued: (listener: RewardCardIssuedListener) => () => void;
  rewardBadgeCount: number;
}

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

function formatStatus(status?: string): string {
  if (!status) return 'updated';
  const code = String(status).toUpperCase();
  if (code === 'PENDING_RESTAURANT_ACCEPTANCE') return 'Waiting for restaurant';
  return status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
}

function parseWaitMinutes(data: Record<string, unknown>): number | undefined {
  const raw = data.approxWaitTime ?? data.preparationTime ?? data.waitTime;
  if (raw == null) return undefined;
  const minutes = Number(raw);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : undefined;
}

export const RealtimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAppSelector((state) => state.user);
  const { showToast } = useToast();
  const tableListeners = useRef<Set<TableRequestStatusListener>>(new Set());
  const orderListeners = useRef<Set<OrderStatusListener>>(new Set());
  const waitTimeListeners = useRef<Set<OrderWaitTimeListener>>(new Set());
  const queueListeners = useRef<Set<QueueTokenUpdateListener>>(new Set());
  const rewardListeners = useRef<Set<RewardCardIssuedListener>>(new Set());
  const [rewardBadgeCount, setRewardBadgeCount] = useState(0);
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;

  const subscribeTableRequestStatus = useCallback((listener: TableRequestStatusListener) => {
    tableListeners.current.add(listener);
    return () => tableListeners.current.delete(listener);
  }, []);

  const subscribeOrderStatus = useCallback((listener: OrderStatusListener) => {
    orderListeners.current.add(listener);
    return () => orderListeners.current.delete(listener);
  }, []);

  const subscribeOrderWaitTime = useCallback((listener: OrderWaitTimeListener) => {
    waitTimeListeners.current.add(listener);
    return () => waitTimeListeners.current.delete(listener);
  }, []);

  const subscribeQueueTokenUpdates = useCallback((listener: QueueTokenUpdateListener) => {
    queueListeners.current.add(listener);
    return () => queueListeners.current.delete(listener);
  }, []);

  const subscribeRewardCardIssued = useCallback((listener: RewardCardIssuedListener) => {
    rewardListeners.current.add(listener);
    return () => rewardListeners.current.delete(listener);
  }, []);

  useEffect(() => {
    const handleMessage = (message: { data?: Record<string, unknown> }) => {
      const data = message.data;
      if (!data?.flag) return;
      const flag = String(data.flag);

      if (flag === 'TABLE_REQUSET_STATUS') {
        const approved = data.approved === true || data.approved === 'true';
        const payload = {
          approved,
          requestId: data.requestId != null ? Number(data.requestId) : undefined,
        };
        tableListeners.current.forEach((fn) => fn(payload));
        showToastRef.current(
          approved ? 'Your table request was approved' : 'Your table request was declined',
          approved ? 'success' : 'error'
        );
        return;
      }

      if (flag === 'ORDER_ITEM_STATUS_CHANGED' || flag === 'ORDER_ITEM_ADDED') {
        const orderId = data.orderId != null ? Number(data.orderId) : 0;
        const orderStatus = String(data.derivedOrderStatus || data.orderStatus || '').trim() || undefined;
        if (!orderId) return;
        const announce = !(data.announce === false || data.announce === 'false');
        const payload: OrderStatusUpdateEvent = {
          orderId,
          orderStatus,
          status: orderStatus,
          updatedOn: data.ts ?? data.updatedOn,
          orderItemId: data.orderItemId != null ? Number(data.orderItemId) : undefined,
          itemName: data.itemName != null ? String(data.itemName) : undefined,
          itemStatus: data.newStatus != null ? String(data.newStatus) : undefined,
          itemQuantity: data.quantity != null ? Number(data.quantity) : undefined,
          isItemStatusChange: flag === 'ORDER_ITEM_STATUS_CHANGED',
          announce,
          userOrderItems: JSON.stringify([{
            id: data.orderItemId,
            itemName: data.itemName,
            status: data.newStatus,
            quantity: data.quantity,
            active: true,
          }]),
        };
        orderListeners.current.forEach((fn) => fn(payload));
        if (flag === 'ORDER_ITEM_STATUS_CHANGED' && announce) {
          const itemName = String(data.itemName || 'Item');
          const statusLabel = String(data.newStatus || '').replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
          const ready = String(data.newStatus || '').toUpperCase() === 'READY';
          showToastRef.current(
            ready ? `${itemName} is ready!` : `${itemName}: ${statusLabel}`,
            ready ? 'success' : 'info'
          );
        }
        return;
      }

      if (flag === 'SHOW_UPDATED_USER_ORDER_STATUS') {
        const orderId = data.orderId != null ? Number(data.orderId) : 0;
        const orderStatus = String(data.orderStatus || data.status || '').trim() || undefined;
        const deliveryStatus = data.deliveryStatus != null ? String(data.deliveryStatus) : undefined;
        if (!orderId || (!orderStatus && !deliveryStatus)) return;
        const orderCode = data.orderCode != null ? String(data.orderCode) : undefined;
        const payload: OrderStatusUpdateEvent = {
          orderId,
          orderCode,
          orderStatus,
          status: orderStatus,
          updatedOn: data.updatedOn,
          approxWaitTime: parseWaitMinutes(data),
          userOrder: data.userOrder as string | undefined,
          userOrderItems: data.userOrderItems as string | undefined,
          deliveryStatus,
        };
        orderListeners.current.forEach((fn) => fn(payload));
        const orderRef = orderCode ?? `#${orderId}`;
        const toastLabel = orderStatus
          ? formatStatus(orderStatus)
          : formatStatus(deliveryStatus);
        showToastRef.current(`Order ${orderRef}: ${toastLabel}`, 'info');
        return;
      }

      if (flag === 'SHOW_USER_ORDER_WAIT_TIME') {
        const orderId = data.orderId != null ? Number(data.orderId) : 0;
        if (!orderId) return;
        const payload: OrderWaitTimeUpdateEvent = {
          orderId,
          approxWaitTime: parseWaitMinutes(data),
          approxWaitDateTime:
            data.approxWaitDateTime != null ? String(data.approxWaitDateTime) : undefined,
        };
        waitTimeListeners.current.forEach((fn) => fn(payload));
        const minutes = payload.approxWaitTime;
        showToastRef.current(
          minutes ? `Updated wait time: about ${minutes} minutes` : 'Estimated wait time was updated',
          'info'
        );
        return;
      }

      if (flag === 'USER_TABLE_SERVICE_REQUEST') {
        const spaceLabel = data.spaceLabel != null ? String(data.spaceLabel) : null;
        const serviceLabel = data.serviceLabel != null ? String(data.serviceLabel) : (data.service != null ? String(data.service) : 'help');
        const body = spaceLabel ? `${spaceLabel} requested ${serviceLabel}` : `Table assistance: ${serviceLabel}`;
        showToastRef.current(body, 'info');
        return;
      }

      if (flag === 'SHOW_USER_TABLE_UPDATED_STATUS') {
        const tableNumber = data.tableNumber != null ? String(data.tableNumber) : '';
        showToastRef.current(
          tableNumber ? `${data.spaceNoun || 'Table'} ${tableNumber} assigned` : 'Table assignment updated',
          'success'
        );
        return;
      }

      if (flag === 'REWARD_CARD_ISSUED') {
        const payload: RewardCardIssuedEvent = {
          cardId: data.cardId != null ? Number(data.cardId) : undefined,
          flowType: data.flowType != null ? String(data.flowType) : undefined,
          rewardLabel: data.rewardLabel != null ? String(data.rewardLabel) : undefined,
          badgeCount: data.badgeCount != null ? Number(data.badgeCount) : undefined,
        };
        rewardListeners.current.forEach((fn) => fn(payload));
        if (payload.badgeCount != null) {
          setRewardBadgeCount(payload.badgeCount);
        }
        showToastRef.current('A reward card is waiting for you! Scratch it in My Rewards.', 'success');
        return;
      }

      if (
        flag === 'QUEUE_TOKEN_READY'
        || flag === 'QUEUE_TOKEN_CALLED'
        || flag === 'QUEUE_TOKEN_ISSUED'
        || flag === 'QUEUE_TOKEN_RECALLED'
        || flag === 'QUEUE_TOKEN_CANCELLED'
        || flag === 'QUEUE_TOKEN_NO_SHOW'
        || flag === 'QUEUE_TOKEN_SEATED'
      ) {
        const token = (data.token || {}) as Record<string, unknown>;
        const payload = {
          tokenLabel: token.tokenLabel != null ? String(token.tokenLabel) : undefined,
          status: token.status != null ? String(token.status) : undefined,
          position: token.position != null ? Number(token.position) : undefined,
          queueType: token.queueType != null ? String(token.queueType) : undefined,
          publicRef: token.publicRef != null ? String(token.publicRef) : undefined,
        };
        queueListeners.current.forEach((fn) => fn(payload));
        if (flag === 'QUEUE_TOKEN_READY' && payload.tokenLabel) {
          showToastRef.current(`Token ${payload.tokenLabel} is ready`, 'success');
        } else if (flag === 'QUEUE_TOKEN_CALLED' && payload.tokenLabel) {
          showToastRef.current(`Token ${payload.tokenLabel} is being called`, 'info');
        }
      }
    };

    return cometService.onMessage(handleMessage);
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      cometService.disconnect();
      return undefined;
    }
    const userId = typeof user.id === 'number' ? user.id : parseInt(String(user.id), 10);
    if (!userId || Number.isNaN(userId)) return undefined;

    const connect = () => {
      cometService.connect(userId).catch((err) => {
        console.error('[CometD] connect failed', err);
      });
    };

    connect();

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        connect();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated, user?.id]);

  return (
    <RealtimeContext.Provider
      value={{ subscribeTableRequestStatus, subscribeOrderStatus, subscribeOrderWaitTime, subscribeQueueTokenUpdates, subscribeRewardCardIssued, rewardBadgeCount }}
    >
      {children}
    </RealtimeContext.Provider>
  );
};

export function useRealtime(): RealtimeContextValue {
  const ctx = useContext(RealtimeContext);
  if (!ctx) {
    throw new Error('useRealtime must be used within RealtimeProvider');
  }
  return ctx;
}
